<template>
    <ui-modal 
        :icon-circle="true"
        title="Download CSV XLSX"
        icon="fal fa-file-alt"
        name="download-csv-xlsx"
    >
        <div class="container-download-csv-xlsx-modal">
            <span class="container-download-csv-xlsx-modal-x" @click="closeModal">
                <i class="far fa-times" />
            </span>

            <div class="container-download-csv-xlsx-modal-title">
                Seu relatório CSV está pronto para download, escolha uma das opções disponíveis abaixo.

                <br />
                <br />
                <p>
                    <span class="infos">
                        A conversão do arquivo para XLSX pode demorar alguns minutos dependendo da quantidade de dados. 
                        Deixe o modal aberto para que o relatório finalize a conversão, enquanto isso você pode navegar
                        pelas outras telas do sistema em outra aba.
                    </span>
                </p>
            </div>

            <div class="container-download-csv-xlsx-modal-actions">
                <ui-button
                    :disabled="disabled.csv"
                    :loading="loader.csv"
                    design="md primary mw-150"
                    @click="downloadCSV"
                >
                    <em class="fal fa-download" />
                    Baixar CSV
                </ui-button>

                <ui-button
                    :disabled="disabled.xls"
                    :loading="loader.xls"
                    design="md success mw-150"
                    @click="downloadXLS"
                >
                    <em class="fal fa-download" />
                    {{ xlsButtonLabel }}
                </ui-button>
                
            </div>
        </div>
    </ui-modal>
</template>

<script>
    import { uiModal                 } from "@/components/ui/modal"
    import { uiButton                } from "@/components/ui/buttons"
    import { storage                 } from '@/services/storage'
    import { helpersMixin, utilMixin } from '@/mixins'
    import { reportsCategory         } from '@/services/axios'


    const defaultReport = () => ({
        name: '',
        csv: {
            query_id: null,
            url: ''
        },
        xls: {
            job_id: null,
            url: ''
        },
        status: 'generating'
    })

    const defaultLoader = () => ({
        csv: true,
        xls: false
    })

    const defaultConfig = () => ({
        category: '',
        index: null
    })

    const defaultDisabled = () => ({
        csv: false,
        xls: true
    })

    export default {
        name: 'ui-reports-download-csv-xlsx',

        components: {
            uiModal,
            uiButton,
        },

        mixins: [helpersMixin, utilMixin],

        data() {
			return {
                config:   { ...defaultConfig()   },
                report:   { ...defaultReport()   },
                loader:   { ...defaultLoader()   },
                disabled: { ...defaultDisabled() },
                observer: null
			}
		},

        computed: {
            xlsButtonLabel() {
                return this.report?.xls?.url ? 'Baixar XLSX' : 'Gerar XLSX'
            }
        },

        mounted() {
            this.$root.$on('disable-loading-csv', payload => {
                this.loader.csv = payload
            })

            this.$root.$on('download-csv', (payload, config) => {
                if (payload && config) {
                    this.report = { ...payload }
                    this.config = { ...config }
                    if (this.report.status === 'generated') {
                        this.disabled.xls = false
                        this.loader.csv = false
                        this.disabled.csv = false

                    } else {
                        this.observeContract()
                    }
                }
            })

            this.$root.$on('modal-close-download-csv-xlsx', () => {
                clearInterval(this.observer)
                this.config     = { ...defaultConfig()   }
                this.report     = { ...defaultReport()   }
                this.loader     = { ...defaultLoader()   }
                this.disabled   = { ...defaultDisabled() }
            })
        },

        beforeDestroy() {
            this.$root.$off('modal-close-download-csv-xlsx')
            this.$root.$off('download-csv')
            this.$root.$off('disable-loading-csv')
        },

        methods: {
            /**
             * Event handler que trata o evento de fechar o modal quando desejado
             */
            closeModal() {
                if (this.loader.csv || this.loader.xls) return

                this.$root.$emit('modal-close-download-csv-xlsx')
            },
            /**
             * Solicita o download do arquivo .CSV gerado para o relatório
             */
            downloadCSV() {
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: "vue-relatorio-planilhas-new",
                    action: "click",
                    label: "Modal - Baixar CSV"
                })

                this.$mx_dll(this.report.csv.url)
                this.$root.$emit('download-csv-xlsx-modal-success', { ...this.config })
                this.closeModal()
            },
            /**
             * Solicita a geração e download do arquivo .XLS à partir do .CSV gerado
             */
             downloadXLS: async function () {
                try {
                    this.$gtm.trackEvent({
                        event: "fac_button_click",
                        category: "vue-relatorio-planilhas-new",
                        action: "click",
                        label: "Modal - Baixar XLS"
                    })

                    if (this.report.xls.url) {
                        this.downloadExistingXLS()
                    } else await this.convertCSVtoXLS()

                } catch (error) {
                    if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                        console.log(error);
                        this.loader.xls = false;
                }
            },
            /**
             * Faz download da planilha XLS Caso a url da mesma exista 
             * e esteja definida em @param this.report.xls.url
             */
            downloadExistingXLS: function () {
                this.$mx_dll(this.report.xls.url);
                this.$root.$emit('download-csv-xlsx-modal-success', { ...this.config })
                this.closeModal()
            },

            /**
             * Chamada que converte a planilha .CSV para .XLS 
             * enviando o @param reportName no payload e obtendo o @param jobId
             */

            convertCSVtoXLS: async function () {
                this.loader.xls = true;
                const params = {
                    reportName: this.report.name,
                    csvUrl: this.report.csv.url
                };

                /**
                 * Determina se a requisição será feita no Data Lake com base na instância da empresa.
                 */
                const getStorage = JSON.parse(storage.get('api_config'))
      			const instance = getStorage.instance
                const reportsCategoryInstance = await reportsCategory.defineVersionReports(instance)
                const response = await reportsCategoryInstance.createV2('convert', params)

                if (response.data?.data?.jobId) {
                    await this.waitForConversion(response.data.data.jobId, params)
                } else {
                    this.report.xls.url = response.data?.data?.xlsxUrl
                    this.saveReport()
                    this.loader.xls = false
                }
            },

            /**
             * Loop que realiza a tentativa de busca do arquivo da planilha no cdn 
             * usando como identificador o @param jobId
             */
            waitForConversion: async function (jobId, params) {
                const interval = setInterval(async () => {
                    try {
                        const instance = JSON.parse(storage.get('api_config')).instance;
                        const reportsCategoryInstance = await reportsCategory.defineVersionReports(instance)

                        const response = await reportsCategoryInstance.createV2(`search/${jobId}`, params);
                        if (response.data && response.data.status === 'success') {
                            this.report.xls.url = response.data?.data?.xlsxUrl;
                            this.saveReport();
                            clearInterval(interval);
                            this.loader.xls = false;
                        } else if (response.data.status === '404') {
                            clearInterval(interval);
                            this.$toastr.e('Erro ao gerar planilha XLS. Arquivo não encontrado. Tente Novamente.')
                            this.loader.xls = false;
                            this.saveReport();
                        }
                    } catch (error) {
                        clearInterval(interval);
                        if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                            console.log(error);
                            this.loader.xls = false;
                    }
                }, 5000);
            },

            /**
             * Um interval que chama a rota /exists do microservice à cada 5s para verificar
             * se o relatório já foi gerado
             */
            async observeContract() {
                const params = { reportName: this.report.name }
                const instance = JSON.parse(storage.get('api_config')).instance;
                const reportsCategoryInstance = await reportsCategory.defineVersionReports(instance)
                
                this.observer = setInterval(() => {
                    reportsCategoryInstance.createV2(`exists/${this.report.csv.query_id}`, params)
                        .then(response => {
                            if (response.data && response.data.status === 'success') {
                                this.report.csv.url = response.data?.data?.csvUrl
                                this.report.status = 'generated'
                                this.saveReport()
                                clearInterval(this.observer)
                                this.disabled.xls = false
                                this.loader.csv = false
                                this.disabled.csv = false
                            }
                        })
                        .catch(error => {
							// medida paliativa enquanto o backend não retorna status de warning
							const loadingMessage = 'Por favor, aguarde. O arquivo está em processo de criação.'
							
							if (error?.data?.msg === loadingMessage) {
    						    this.$toastr.w(error.data.msg);
    						} else if (error?.data?.msg) {
    						    this.$toastr.e(error.data.msg);
    						}

							console.log(error)
                            clearInterval(this.observer)
                            this.loader.csv = false
                            this.disabled.csv = true
                        })
                }, 5000)
            },
            /**
             * Salva os dados atualizados do relatório gerado no localStorage
             */
            saveReport() {
                // Carrega os dados do relatório atual
                const storageKey = `${this.config.category}_card_fields_${this.config.index}`
                let storageData = storage.get(storageKey)
                storageData = storageData && this.isJSON(storageData)
                    ? JSON.parse(storageData)
                    : []
                // Atualiza os dados de geração do relatório atual no localStorage
                const index = storageData.findIndex(item => item.fieldName === 'generating_file')
                if (index >= 0) {
                    storageData[index] = { ...storageData[index], report: { ...this.report } }
                    storage.set(storageKey, JSON.stringify(storageData))
                    this.$root.$emit(`${this.config.category}-save-fields-${this.config.index}`)
                }
            }
        },
    }
</script>

<style lang="stylus" scoped>
    @import './styles.styl';
</style>